import { QA_STATUS } from '../types/visitData.types';

// Simplified filter values for dropdown (sent to API layer)
export const QA_STATUS_FILTER = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED_FAILED: 'cancelled_failed',
} as const;

// Simplified display labels for QA statuses
export const QA_STATUS_DISPLAY = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED_FAILED: 'Cancelled/Failed',
} as const;

/**
 * Maps simplified UI filter value to actual backend statuses
 */
export const mapQAStatusFilterToBackend = (filterValue: string): string[] => {
  switch (filterValue) {
    case QA_STATUS_FILTER.NOT_STARTED:
      return [QA_STATUS.NOT_STARTED];
    case QA_STATUS_FILTER.IN_PROGRESS:
      return [
        QA_STATUS.INITIALIZED,
        QA_STATUS.PATIENT_INFO_SET,
        QA_STATUS.QA_ANALYSIS_GENERATED,
      ];
    case QA_STATUS_FILTER.COMPLETED:
      return [QA_STATUS.COMPLETED];
    case QA_STATUS_FILTER.CANCELLED_FAILED:
      return [QA_STATUS.FAILED, QA_STATUS.CANCELLED];
    default:
      return [];
  }
};

/**
 * Maps backend QA status to simplified display label
 */
export const getQAStatusDisplayLabel = (
  status: string | null | undefined
): string => {
  if (!status) return QA_STATUS_DISPLAY.NOT_STARTED;

  switch (status) {
    case QA_STATUS.NOT_STARTED:
      return QA_STATUS_DISPLAY.NOT_STARTED;
    case QA_STATUS.INITIALIZED:
    case QA_STATUS.PATIENT_INFO_SET:
    case QA_STATUS.QA_ANALYSIS_GENERATED:
      return QA_STATUS_DISPLAY.IN_PROGRESS;
    case QA_STATUS.COMPLETED:
      return QA_STATUS_DISPLAY.COMPLETED;
    case QA_STATUS.FAILED:
    case QA_STATUS.CANCELLED:
      return QA_STATUS_DISPLAY.CANCELLED_FAILED;
    default:
      return status;
  }
};

/**
 * Returns badge color classes for simplified QA status
 */
export const getQAStatusBadgeStyles = (
  status: string | null | undefined
): string => {
  const displayLabel = getQAStatusDisplayLabel(status);

  switch (displayLabel) {
    case QA_STATUS_DISPLAY.COMPLETED:
      return 'bg-green-100 text-green-700 border-green-200';
    case QA_STATUS_DISPLAY.IN_PROGRESS:
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case QA_STATUS_DISPLAY.CANCELLED_FAILED:
      return 'bg-red-100 text-red-700 border-red-200';
    case QA_STATUS_DISPLAY.NOT_STARTED:
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

/**
 * Check if status is in progress (for UI logic)
 */
export const isQAStatusInProgress = (status: string | undefined): boolean => {
  return (
    status === QA_STATUS.NOT_STARTED ||
    status === QA_STATUS.INITIALIZED ||
    status === QA_STATUS.PATIENT_INFO_SET ||
    status === QA_STATUS.QA_ANALYSIS_GENERATED
  );
};

/**
 * Check if status is completed (for UI logic)
 */
export const isQAStatusCompleted = (status: string | undefined): boolean => {
  return status === QA_STATUS.COMPLETED;
};
